import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import * as usePrismic from '@cobliteam/hooks/src/useEntity/usePrismic';
import { isFilled } from '@cobliteam/prismic';
import type { BlogPostDocumentDataRelationsItem, RichTextSliceDefaultPrimary } from '@cobliteam/prismic/src/types/support-cobli.generated';
import { DrawerClose } from '@cobliteam/ui/components';
import Button from '@cobliteam/ui/components/Button';
import Tabs, { TabsContent, TabsList, TabsTrigger } from '@cobliteam/ui/components/Tabs';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { useState } from 'react';
import PageContent from './components/PageContent';
import RelationDetail from './components/RelationDetail';
import RelationsList from './components/RelationsList';

const AppInfo = ({ supportUID }: { supportUID: string }) => {
  const { sidebar } = useDashboard();
  const [selectedRelation, setSelectedRelation] = useState<BlogPostDocumentDataRelationsItem | null>(null);

  const {
    data: blogPost,
    isLoading: isLoadingBlogPost,
    error,
  } = usePrismic.useGetSupportPostByUID({
    dashboardRouterId: supportUID,
    options: { staleTime: Number.POSITIVE_INFINITY, gcTime: Number.POSITIVE_INFINITY },
  });

  // content
  const icon = sidebar.route?.sidebar.icon;
  const title = blogPost?.data.title;
  const resume = blogPost?.data.resume;
  const content = blogPost?.data.slices[0]?.primary as RichTextSliceDefaultPrimary;

  // relations
  const relations = blogPost?.data.relations as BlogPostDocumentDataRelationsItem[];

  const hasValidRelations = relations && relations.length > 0 && relations.some((relation) => isFilled.contentRelationship(relation.post) && relation.post.data?.title);

  if (isLoadingBlogPost)
    return (
      <div className='flex flex-1 flex-col items-center justify-center'>
        <Icon name='loader' size='sm' className='-ml-1 size-4' aria-hidden='true' />
      </div>
    );

  return (
    <Tabs defaultValue='page' className='flex flex-1 flex-col overflow-hidden'>
      <TabsList size='md' className='flex items-center justify-between pr-2'>
        <div>
          <TabsTrigger value='page' className='inline-flex gap-2'>
            <Icon name='file-text' size='sm' className='-ml-1 size-4' aria-hidden='true' />
            Sobre a página
          </TabsTrigger>
          {hasValidRelations && (
            <TabsTrigger value='tutorials' className='inline-flex gap-2'>
              <Icon name='flag' size='sm' className='-ml-1 size-4' aria-hidden='true' />
              Guia de uso
            </TabsTrigger>
          )}
        </div>
        <DrawerClose asChild>
          <Button variant='ghost' size='sm' aria-label='Fechar'>
            <Icon name='x' />
          </Button>
        </DrawerClose>
      </TabsList>
      <TabsContent value='page' className='scrollbar-thin scrollbar-thumb-gray-8 scrollbar-track-gray-2 overflow-y-auto overflow-x-hidden p-2'>
        <PageContent icon={icon as IconNames} title={title || ''} resume={resume} content={content} />
      </TabsContent>
      {hasValidRelations && (
        <TabsContent value='tutorials' className='scrollbar-thin scrollbar-thumb-gray-8 scrollbar-track-gray-2 overflow-y-auto overflow-x-hidden p-2'>
          {selectedRelation ? (
            <RelationDetail selectedRelation={selectedRelation} onBack={() => setSelectedRelation(null)} />
          ) : (
            <RelationsList relations={relations} onRelationSelect={setSelectedRelation} />
          )}
        </TabsContent>
      )}
    </Tabs>
  );
};

export default AppInfo;
